import type { MountDecisionEventV1, ResolvedMountPoint } from './mountContract';
import { HUB_LAYOUT_V1 } from './tokens/hubLayout';

export type HubLayoutV1Decision = 'applied' | 'not-hub' | 'invalid-shell';

export interface HubLayoutV1Result {
  readonly decision: HubLayoutV1Decision;
  readonly shell: HTMLElement | null;
}

export type HubLayoutV1ResolutionContext = Pick<MountDecisionEventV1, 'decision' | 'pathFamily'>;

const activeShellByCarrier = new WeakMap<HTMLElement, HTMLElement>();

function deactivateHubLayoutV1(carrier: HTMLElement): void {
  const previousShell = activeShellByCarrier.get(carrier);
  previousShell?.classList.remove(HUB_LAYOUT_V1.shellModifierClass);
  activeShellByCarrier.delete(carrier);

  carrier.classList.remove(HUB_LAYOUT_V1.carrierModifierClass);
  carrier.parentElement?.classList.remove(HUB_LAYOUT_V1.shellModifierClass);
}

function exactDirectChild(
  shell: HTMLElement,
  tagName: 'HEADER' | 'NAV',
  className: string
): HTMLElement | null {
  const matches = Array.from(shell.children).filter(
    (child): child is HTMLElement =>
      child instanceof HTMLElement &&
      child.tagName === tagName &&
      child.classList.contains(className)
  );
  return matches.length === 1 ? matches[0] : null;
}

function exactIndexLink(shell: HTMLElement, nav: HTMLElement): HTMLElement | null {
  const matches = Array.from(
    shell.querySelectorAll<HTMLElement>(`.${HUB_LAYOUT_V1.indexLinkClass}`)
  );
  const match = matches.length === 1 ? matches[0] : null;
  const href = match?.getAttribute('href')?.trim();

  return match?.tagName === 'A' && href !== undefined && href.length > 0 && nav.contains(match)
    ? match
    : null;
}

/**
 * Activate the shared Hub shell after either SurfaceMountV1 or the bounded
 * pre-contract compatibility identity passes, and only when the renderer's
 * exact direct semantic topology is present. No host-theme node is queried or
 * changed, and fallback-created mounts are never promoted to Hub surfaces.
 */
export function installHubLayoutV1(
  mount: Pick<ResolvedMountPoint, 'carrier' | 'surface' | 'source' | 'questionId'>,
  context: HubLayoutV1ResolutionContext
): HubLayoutV1Result {
  // Bootstrap/HMR re-entry must fail closed even when a prior invocation (or
  // malformed preloaded markup) left app-owned activation classes behind.
  deactivateHubLayoutV1(mount.carrier);

  const isContractHub = context.decision === 'contract' && mount.surface === 'hub';
  const isRecognizedPreContractMount =
    context.decision === 'old-id' &&
    context.pathFamily === 'hub' &&
    mount.surface === null &&
    mount.questionId === null &&
    (mount.source === 'mspl-mount' || mount.source === 'legacy-mount');
  if (!isContractHub && !isRecognizedPreContractMount) {
    return { decision: 'not-hub', shell: null };
  }

  const shell = mount.carrier.parentElement;
  const header = shell && exactDirectChild(shell, 'HEADER', HUB_LAYOUT_V1.headerClass);
  const nav = shell && exactDirectChild(shell, 'NAV', HUB_LAYOUT_V1.navClass);
  if (
    shell === null ||
    shell.tagName !== 'ARTICLE' ||
    !shell.classList.contains(HUB_LAYOUT_V1.shellClass) ||
    header === null ||
    nav === null ||
    exactIndexLink(shell, nav) === null
  ) {
    // An explicit contract Hub with invalid renderer topology is actionable.
    // A recognized pre-contract ID can also appear on non-Hub legacy pages, so
    // topology mismatch there is an intentional no-op rather than a warning.
    return { decision: isContractHub ? 'invalid-shell' : 'not-hub', shell: null };
  }

  shell.classList.add(HUB_LAYOUT_V1.shellModifierClass);
  mount.carrier.classList.add(HUB_LAYOUT_V1.carrierModifierClass);
  activeShellByCarrier.set(mount.carrier, shell);
  return { decision: 'applied', shell };
}
